import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Building2, ShieldCheck } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/authStore';
import { Avatar } from '@/components/ui/Avatar';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV: NavItem[] = [
  { to: '/platform', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/platform/organisations', label: 'Organisations', icon: Building2 },
];

export interface PlatformSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

function NavList({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.to === '/platform'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded px-3 py-2 text-sm font-medium transition-colors',
                'hover:bg-primary-light hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive ? 'bg-primary-light text-primary' : 'text-white/80'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/** Mirrors Sidebar.tsx's responsive drawer pattern, for the cross-org
 * platform-admin area — a deliberately separate, smaller nav rather than
 * threading this identity through the org-scoped Sidebar. */
export function PlatformSidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: PlatformSidebarProps) {
  const { platformAdmin } = useAuthStore();
  const isSuperAdmin = platformAdmin?.role === 'super_admin';
  const displayName = platformAdmin?.display_name || platformAdmin?.email || 'Platform Admin';

  const content = (
    <>
      <div className="flex items-center gap-2 px-4 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-primary">D</div>
        {!collapsed && <span className="text-lg font-semibold tracking-tight text-white">DECIXA Platform</span>}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        <NavList items={NAV} collapsed={collapsed} />

        {isSuperAdmin && (
          <div className={cn('mt-4 border-t border-white/10 pt-4', collapsed ? 'px-0' : 'px-3')}>
            {!collapsed && <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/50">Super Admin</p>}
            <NavList items={[{ to: '/platform/admins', label: 'Admins', icon: ShieldCheck }]} collapsed={collapsed} />
          </div>
        )}
      </nav>

      <div className={cn('flex items-center gap-2 border-t border-white/10 p-3', collapsed && 'justify-center')}>
        <Avatar name={displayName} size="xs" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">{displayName}</p>
            <p className="text-xs text-white/50">Platform Admin</p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="hidden border-t border-white/10 px-4 py-3 text-left text-xs font-medium text-white/60 hover:text-white md:block"
      >
        {collapsed ? '»' : '« Collapse'}
      </button>
    </>
  );

  return (
    <>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col bg-primary transition-all duration-300 md:flex',
          collapsed ? 'w-20' : 'w-64'
        )}
      >
        {content}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={onMobileClose} aria-hidden="true" />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-primary">{content}</aside>
        </div>
      )}
    </>
  );
}
